'use client';

import { useEffect, useRef } from 'react';
import { getModelContext, type WebMcpTool } from './modelContext';

/**
 * Registers a fixed set of WebMCP tools for as long as the calling component is mounted.
 *
 * Registration happens once per tool name, but `execute` is dispatched through a ref, so a
 * tool registered on mount still closes over the newest render's state. Without that the
 * agent would mutate whatever filters were active when the shell first mounted.
 *
 * No-ops when the browser has no `document.modelContext`, so callers never need to feature-detect.
 */
export function useWebMcpTools(tools: Array<WebMcpTool>): void {
  const toolsRef = useRef(tools);
  toolsRef.current = tools;

  const toolNames = tools.map((tool) => tool.name).join(',');

  useEffect(() => {
    const modelContext = getModelContext();
    if (!modelContext) return undefined;

    const controller = new AbortController();

    for (const registered of toolsRef.current) {
      const { name } = registered;

      void Promise.resolve(
        modelContext.registerTool(
          {
            ...registered,
            execute: (args, context) => {
              const current = toolsRef.current.find((tool) => tool.name === name) ?? registered;
              return current.execute(args, context);
            },
          },
          { signal: controller.signal },
        ),
      ).catch(() => {
        // A rejected registration (duplicate name, revoked trial token) must not break the page.
        // The tool is simply absent, which is the same state as a browser without WebMCP.
      });
    }

    return () => controller.abort();
  }, [toolNames]);
}
